import { Routes } from '@angular/router';
import { authGuard } from '@app/guards/auth/auth-guard';
import { featureGuard } from '@app/guards/feature/feature-guard';
import { networkGuard } from '@app/guards/network/network-guard';
import { homeBannersResolver } from '@app/pages/home/home-banners.resolver';
import { homeProductsResolver } from '@app/pages/home/home-products.resolver';
import { NoInternet } from '@app/pages/no-internet/no-internet';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [networkGuard],
    resolve: { homeBanners: homeBannersResolver, homeProducts: homeProductsResolver },
    loadComponent: () => import('@app/pages/home/home').then((m) => m.Home),
  },
  {
    path: 'loader',
    canActivate: [networkGuard],
    loadComponent: () => import('@app/pages/loader/loader').then((m) => m.Loader),
  },
  {
    path: 'home',
    pathMatch: 'full',
    redirectTo: '',
  },
  {
    path: 'products',
    canActivate: [networkGuard],
    loadComponent: () => import('@app/pages/products/products').then((m) => m.Products),
  },
  {
    path: 'product-details/:productSlug',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/product-details/product-details').then((m) => m.ProductDetails),
  },
  { path: 'my-profile', redirectTo: '/my-profile/my-wallet', pathMatch: 'full' },
  {
    path: 'my-profile/my-subscriptions',
    redirectTo: '/my-profile/my-subscriptions/subscription-list',
    pathMatch: 'full',
  },
  {
    path: 'my-profile/my-orders',
    redirectTo: '/my-profile/my-orders/order-list',
    pathMatch: 'full',
  },
  {
    path: 'my-profile',
    canActivate: [authGuard, networkGuard],
    loadComponent: () => import('@app/pages/my-profile/my-profile').then((m) => m.MyProfile),
    children: [
      {
        path: 'test-reports',
        canMatch: [featureGuard],
        loadComponent: () =>
          import('@app/components/my-profile/test-reports/test-reports').then((m) => m.TestReports),
      },
      {
        path: 'address-book',
        loadComponent: () =>
          import('@app/components/my-profile/address-book/address-book').then((m) => m.AddressBook),
      },
      {
        path: 'edit-profile',
        loadComponent: () =>
          import('@app/components/my-profile/edit-profile/edit-profile').then((m) => m.EditProfile),
      },
      {
        path: 'my-subscriptions',
        loadComponent: () =>
          import('@app/components/my-profile/my-subscriptions/my-subscriptions/my-subscriptions').then(
            (m) => m.MySubscriptions,
          ),
        children: [
          {
            path: 'subscription-list',
            loadComponent: () =>
              import('@app/components/my-profile/my-subscriptions/subscription-list/subscription-list').then(
                (m) => m.SubscriptionList,
              ),
          },
          {
            path: 'subscription-details/:subscriptionId',
            loadComponent: () =>
              import('@app/components/my-profile/my-subscriptions/subscription-details/subscription-details').then(
                (m) => m.SubscriptionDetails,
              ),
          },
        ],
      },
      {
        path: 'my-orders',
        loadComponent: () =>
          import('@app/components/my-profile/orders/orders/orders').then((m) => m.Orders),
        children: [
          {
            path: 'order-list',
            loadComponent: () =>
              import('@app/components/my-profile/orders/order-list/order-list').then(
                (m) => m.OrderList,
              ),
          },
          {
            path: 'order-details/:orderId',
            loadComponent: () =>
              import('@app/components/my-profile/orders/order-details/order-details').then(
                (m) => m.OrderDetails,
              ),
          },
        ],
      },
      {
        path: 'calendar',
        loadComponent: () =>
          import('@app/components/my-profile/calender/calender').then((m) => m.Calender),
      },
      {
        path: 'my-rewards',
        loadComponent: () =>
          import('@app/components/my-profile/my-rewards/rewards/rewards').then((m) => m.Rewards),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@app/components/my-profile/my-rewards/reward-details/reward-details').then(
                (m) => m.RewardDetails,
              ),
          },
          {
            path: 'history',
            loadComponent: () =>
              import('@app/components/my-profile/my-rewards/reward-history/reward-history').then(
                (m) => m.RewardHistory,
              ),
          },
          {
            path: 'tier-benefits',
            loadComponent: () =>
              import('@app/components/my-profile/my-rewards/tier-benefits/tier-benefits').then(
                (m) => m.TierBenefits,
              ),
          },
          {
            path: 'redeem',
            loadComponent: () =>
              import('@app/components/my-profile/my-rewards/redeem-reward/redeem-reward').then(
                (m) => m.RedeemReward,
              ),
          },
          {
            path: 'how-to-earn',
            loadComponent: () =>
              import('@app/components/my-profile/my-rewards/how-earn-reward/how-earn-reward').then(
                (m) => m.HowEarnReward,
              ),
          },
        ],
      },
      {
        path: 'refer-earn',
        loadComponent: () =>
          import('@app/components/my-profile/refer-and-earn/refer-and-earn').then(
            (m) => m.ReferAndEarn,
          ),
      },
      {
        path: 'my-wallet',
        loadComponent: () =>
          import('@app/components/my-profile/my-wallet/wallet/wallet').then((m) => m.Wallet),
      },
      {
        path: 'vacation-mode',
        loadComponent: () =>
          import('@app/components/my-profile/vacation-mode/vacation-mode').then(
            (m) => m.VacationMode,
          ),
      },
      {
        path: 'empty-bottles',
        loadComponent: () =>
          import('@app/components/my-profile/empty-bottles/empty-bottles').then(
            (m) => m.EmptyBottles,
          ),
      },
      {
        path: 'help-support',
        loadComponent: () =>
          import('@app/components/my-profile/help-and-support/help-and-support/help-and-support').then(
            (m) => m.HelpAndSupport,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@app/components/my-profile/help-and-support/help-support-dashboard/help-support-dashboard').then(
                (m) => m.HelpSupportDashboard,
              ),
          },
          {
            path: 'faq-list/:faqCategory',
            loadComponent: () =>
              import('@app/components/my-profile/help-and-support/category-faq-list/category-faq-list').then(
                (m) => m.CategoryFaqList,
              ),
          },
          {
            path: 'report-issue',
            loadComponent: () =>
              import('@app/components/my-profile/help-and-support/report-issue/report-issue').then(
                (m) => m.ReportIssue,
              ),
          },
          {
            path: 'ticket-chat/:ticketId/:ticketStatus',
            loadComponent: () =>
              import('@app/components/my-profile/help-and-support/support-ticket-chat/support-ticket-chat').then(
                (m) => m.SupportTicketChat,
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'data-deletion-policy',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/data-deletion-policy/data-deletion-policy').then(
        (m) => m.DataDeletionPolicy,
      ),
  },
  {
    path: 'about-us',
    canActivate: [networkGuard],
    loadComponent: () => import('@app/pages/about-us/about-us').then((m) => m.AboutUs),
  },
  {
    path: 'privacy-policy',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
  },
  {
    path: 'terms-and-conditions',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/terms-condition/terms-condition').then((m) => m.TermsCondition),
  },
  {
    path: 'privacy-policy-mobile',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
  },
  {
    path: 'terms-and-conditions-mobile',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/terms-condition/terms-condition').then((m) => m.TermsCondition),
  },
  {
    path: 'good-mood-blog',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/good-mood-blog/good-mood-blog').then((m) => m.GoodMoodBlog),
  },
  {
    path: 'good-mood-blog-details/:blogId/:blogCategory',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/good-mood-blog-details/good-mood-blog-details').then(
        (m) => m.GoodMoodBlogDetails,
      ),
  },
  {
    path: 'our-farm',
    canActivate: [networkGuard],
    canMatch: [featureGuard],
    loadComponent: () => import('@app/pages/our-farm/our-farm').then((m) => m.OurFarm),
  },
  {
    path: 'maintenance',
    canActivate: [networkGuard],
    loadComponent: () => import('@app/pages/maintenance/maintenance').then((m) => m.Maintenance),
  },
  {
    path: 'no-internet',
    component: NoInternet,
  },
  {
    path: 'thank-you',
    canActivate: [authGuard, networkGuard],
    loadComponent: () =>
      import('@app/pages/order-thank-you/order-thank-you').then((m) => m.OrderThankYou),
  },
  {
    path: 'payment-status',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/payment-status/payment-status').then((m) => m.PaymentStatus),
  },
  {
    path: 'grievance-policy',
    canActivate: [networkGuard],
    loadComponent: () =>
      import('@app/pages/grievance-policy/grievance-policy').then((m) => m.GrievancePolicy),
  },
  {
    path: 'not-found',
    canActivate: [networkGuard],
    loadComponent: () => import('@app/pages/not-found/not-found').then((m) => m.NotFound),
  },
  {
    path: '**',
    redirectTo: 'not-found',
  }
];
